import { useState } from "react";
import { useTranslation } from "@localization/useTranslation";
import { RiskAreaFrameChangeImpact, RiskAreaSaveModel } from "../RiskAreaDialog.types";
import { findCollapses, isValidMapping, proportionalMapping } from "./scaleMapping";

export interface FrameChangeFigure {
    label: string;
    value: number;
}

export interface MappingRow {
    sourceLevel: number;
    sourceLabel: string;
    targetLevel: number;
}

export function useFrameChangeFigures(impact: RiskAreaFrameChangeImpact | undefined) {
    const { translateString } = useTranslation();

    // Only the consequences that actually apply are listed: a row reading zero invites the reader to skim past
    // the rows that do not.
    const figures: Array<FrameChangeFigure> = impact
        ? [
              { label: translateString("riskAreaFrameChangeAssessmentsReBased"), value: impact.assessmentsReBased },
              { label: translateString("riskAreaFrameChangeAssessmentsChangingLevel"), value: impact.assessmentsChangingLevel },
              { label: translateString("riskAreaFrameChangeNewlyOverAppetite"), value: impact.newlyOverAppetite },
              { label: translateString("riskAreaFrameChangeNoLongerOverAppetite"), value: impact.noLongerOverAppetite },
              { label: translateString("riskAreaFrameChangeAcceptancesNeedingReconfirmation"), value: impact.acceptancesNeedingReconfirmation },
              { label: translateString("riskAreaFrameChangeOpenPlansNeedingReconfirmation"), value: impact.openPlansNeedingReconfirmation },
          ].filter((figure) => figure.value > 0)
        : [];

    return { figures, hasImpact: figures.length > 0 };
}

// The conversion rule for the scores already given: how each level of the old scale is carried onto the new one.
// It is the only thing left to settle by the time this step opens, because the criteria themselves were authored
// on the form. Kept out of the form's own state so that abandoning the confirmation abandons the rule with it.
export function useScaleMapping(original: RiskAreaSaveModel | undefined, proposed: RiskAreaSaveModel) {
    const { translateString } = useTranslation();
    const fromScale = original?.scale ?? proposed.scale;
    const toScale = proposed.scale;
    const isReBasing = fromScale !== toScale;

    const [consequence, setConsequence] = useState<Array<number>>([]);
    const [probability, setProbability] = useState<Array<number>>([]);

    // Seeded when the step opens rather than derived, because from that point the rule is the user's to edit.
    const seed = () => {
        setConsequence(proportionalMapping(fromScale, toScale));
        setProbability(proportionalMapping(fromScale, toScale));
    };

    const consequenceValid = isValidMapping(consequence, fromScale, toScale);
    const probabilityValid = isValidMapping(probability, fromScale, toScale);

    // Shown as information rather than as an error: merging levels is the expected outcome of shrinking a scale,
    // but it silently erases a distinction the customer drew, so it has to be said out loud.
    const collapseWarnings = original
        ? [
              ...toCollapseWarnings(consequence, original.consequenceLevels, translateString("consequence")),
              ...toCollapseWarnings(probability, original.probabilityLevels, translateString("probability")),
          ]
        : [];

    return {
        isReBasing,
        seed,
        fromScale,
        toScale,
        model: { consequence, probability },
        isValid: !isReBasing || (consequenceValid && probabilityValid),
        consequenceValid,
        probabilityValid,
        consequenceRows: toRows(consequence, original?.consequenceLevels ?? []),
        probabilityRows: toRows(probability, original?.probabilityLevels ?? []),
        setConsequenceTarget: (sourceIndex: number, targetLevel: number) => setConsequence(replaceAt(consequence, sourceIndex, targetLevel)),
        setProbabilityTarget: (sourceIndex: number, targetLevel: number) => setProbability(replaceAt(probability, sourceIndex, targetLevel)),
        collapseWarnings,
    };
}

function toRows(mapping: Array<number>, sourceLabels: Array<string>): Array<MappingRow> {
    return mapping.map((targetLevel, index) => ({ sourceLevel: index + 1, sourceLabel: sourceLabels[index] ?? "", targetLevel }));
}

function toCollapseWarnings(mapping: Array<number>, sourceLabels: Array<string>, axisName: string): Array<string> {
    return findCollapses(mapping).map((collapse) => {
        const merged = collapse.sourceLevels.map((level) => `${level} ${sourceLabels[level - 1] ?? ""}`.trim()).join(", ");
        return `${axisName}: ${merged} → ${collapse.targetLevel}`;
    });
}

function replaceAt(values: Array<number>, index: number, value: number): Array<number> {
    return values.map((current, i) => (i === index ? value : current));
}
